import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { ArrowRight } from "@phosphor-icons/react";
import { COUNTRIES } from "../lib/countries";

function safeNext(raw) {
  if (!raw) return "/dashboard";
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("/") && !decoded.startsWith("//")) return decoded;
  } catch {
    /* noop */
  }
  return "/dashboard";
}

export default function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("ZA");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = safeNext(params.get("next"));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    const res = await register(email, password, name, country);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created");
      navigate(nextPath);
    } else {
      toast.error(res.error || "Registration failed");
    }
  };

  return (
    <div className="max-w-[1440px] mx-auto grid lg:grid-cols-2 border-b border-[var(--border)] min-h-[calc(100vh-4rem)]">
      <div className="p-8 md:p-16 flex items-center order-2 lg:order-1">
        <form onSubmit={onSubmit} className="w-full max-w-sm mx-auto" data-testid="register-form">
          <div className="micro-label">/ Register</div>
          <h1 className="font-display font-black text-4xl mt-1">Start free</h1>
          <p className="text-[var(--fg-2)] text-sm mt-3">
            Already have an account? <Link to={`/login${nextPath !== "/dashboard" ? `?next=${encodeURIComponent(nextPath)}` : ""}`} className="text-[var(--accent)] underline">Sign in</Link>
          </p>

          <label className="block mt-8">
            <span className="micro-label">Name (optional)</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none"
              data-testid="input-name"
            />
          </label>
          <label className="block mt-4">
            <span className="micro-label">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none"
              data-testid="input-email"
            />
          </label>
          <label className="block mt-4">
            <span className="micro-label">Password</span>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none"
              data-testid="input-password"
            />
          </label>
          <label className="block mt-4">
            <span className="micro-label">Country</span>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none appearance-none cursor-pointer"
              data-testid="input-country"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="btn-accent w-full mt-8 inline-flex items-center justify-center gap-2"
            data-testid="btn-register-submit"
          >
            {loading ? "Creating…" : (<>Create account <ArrowRight size={14} weight="bold"/></>)}
          </button>
          <p className="text-[10px] text-[var(--fg-2)] mt-4 font-mono tracking-wider">
            BY REGISTERING YOU AGREE TO USE THIS TOOL RESPONSIBLY.
          </p>
        </form>
      </div>
      <div className="hidden lg:block bg-black relative grain order-1 lg:order-2">
        <div className="absolute inset-0 flex items-end p-12 text-white">
          <div>
            <div className="micro-label text-white/60">/ 15 KB CEILING</div>
            <h2 className="font-display font-black text-5xl mt-2 leading-[0.95]">
              Zero<br/>
              rejections<br/>
              <span className="text-[var(--accent)]">in-game.</span>
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}
